import { Form, SelectProps } from 'antd';

import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { ListEmployeesQueryReduced } from '@/core/domain/enterprise/Employees/Employees.types';
import {
  useEmployeeDetailReducedMutation,
  useEmployeesReduced,
} from '@/core/domain/enterprise/Employees/Employees.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import useSelectOptions from '@/hooks/useSelectOptions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListEmployeesQueryReduced;
  queryEnable?: boolean;
  workOrderNotSelectOtherEstimator?: boolean;
  excludedId?: number;
  selectSingle?: boolean;
};

const EmployeesInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  workOrderNotSelectOtherEstimator = false,
  selectSingle = true,
  excludedId,
  ...props
}) => {
  const { employeeId } = getUserFromLocalStorage();
  const hasFormContext = !!Form.useFormInstance();

  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const isMultiple = props.mode === 'multiple';

  const employeesReducedList = useEmployeesReduced(
    {
      isActive: true,
      rows: 10,
      name: search,
      ...queryFilter,
    },
    (selectClicked || queryEnable) &&
      !props.disabled &&
      !workOrderNotSelectOtherEstimator,
  );

  const employees = flatInfiniteData(employeesReducedList.data);

  const employeeDetail = useEmployeeDetailReducedMutation();

  const employeeOptions = useSelectOptions({
    list: employeesReducedList,
    detail: employeeDetail,
    selectProps: props,
    labelFormatter: (employee) => employee.name,
  });

  const handleDetail = async () => {
    const data = await employeeDetail.mutateAsync(Number(employeeId));

    if (!!props.onChange && data) {
      props.onChange(data.id, {
        value: data.id,
        label: data.name,
      });
    }
  };

  useEffect(() => {
    if (
      employees.length &&
      employees.length === 1 &&
      !props.value &&
      !!props.onChange &&
      hasFormContext &&
      selectSingle
    ) {
      props.onChange(employees[0].id, {
        value: employees[0].id,
        label: employees[0].name,
      });
    }
  }, [employees, hasFormContext, selectSingle]);

  useEffect(() => {
    if (
      workOrderNotSelectOtherEstimator &&
      !!employeeId &&
      !props.disabled &&
      !props.value
    ) {
      handleDetail();
    }
  }, [workOrderNotSelectOtherEstimator, employeeId, props.disabled]);

  return (
    <InfiniteSelect
      fetchNextPage={employeesReducedList.fetchNextPage}
      hasNextPage={employeesReducedList.hasNextPage}
      loading={employeesReducedList.isFetching}
      onSelect={() => setSelectClicked(false)}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      options={employeeOptions.filter(({ value }) => value !== excludedId)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      maxTagTextLength={isMultiple ? 30 : undefined}
      {...props}
    />
  );
};

export default EmployeesInfiniteSelectField;
